import React, { useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import theme from "../../../shared/utils/themeColors";
import { TutorialVideo } from "../types/tutorial-bank.types";
import VideoPlayer from "./VideoPlayer";

interface TutorialVideoUploaderProps {
  video?: TutorialVideo;
  onUpload: (file: File) => void;
  onRemoveVideo: () => void;
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  isUploading: boolean;
  onExternalVideoAdd?: (videoData: Partial<TutorialVideo>) => void;
}

const TutorialVideoUploader: React.FC<TutorialVideoUploaderProps> = ({
  video,
  onUpload,
  onRemoveVideo,
  onTitleChange,
  onDescriptionChange,
  isUploading,
  onExternalVideoAdd,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showExternalLinkInput, setShowExternalLinkInput] = useState(false);
  const [externalVideoUrl, setExternalVideoUrl] = useState("");
  const [externalVideoError, setExternalVideoError] = useState<string | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle drag events
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      handleFileSelection(file);
    }
  };

  // Handle file selection
  const handleFileSelection = (file: File) => {
    // Validate file type
    if (!file.type.startsWith("video/")) {
      alert("Please select a valid video file (MP4 recommended)");
      return;
    }

    // Call the upload handler
    onUpload(file);
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelection(e.target.files[0]);
    }
  };

  // Handle title change
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onTitleChange(e.target.value);
  };

  // Handle description change
  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    onDescriptionChange(e.target.value);
  };

  // Toggle external link input
  const toggleExternalLinkInput = () => {
    setShowExternalLinkInput(!showExternalLinkInput);
    setExternalVideoError(null);
  };

  // Handle external video URL change
  const handleExternalVideoUrlChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setExternalVideoUrl(e.target.value);
    setExternalVideoError(null);
  };

  // Validate and extract video ID from YouTube URL
  const extractYouTubeVideoId = (url: string): string | null => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Validate and extract video ID from Vimeo URL
  const extractVimeoVideoId = (url: string): string | null => {
    const regExp =
      /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/;
    const match = url.match(regExp);
    return match ? match[5] : null;
  };

  // Check if URL is a direct MP4 link
  const isDirectVideoUrl = (url: string): boolean => {
    return (
      url.toLowerCase().endsWith(".mp4") ||
      url.toLowerCase().endsWith(".mov") ||
      url.toLowerCase().endsWith(".webm") ||
      url.includes("cloudfront.net") ||
      url.includes("amazonaws.com") ||
      url.includes("blob:") ||
      url.includes("/video/")
    );
  };

  const getVimeoThumbnail = async (videoId: string): Promise<string | null> => {
    try {
      const response = await fetch(
        `https://vimeo.com/api/v2/video/${videoId}.json`
      );
      if (!response.ok) return null;

      const data = await response.json();
      return data[0]?.thumbnail_large || null;
    } catch (error) {
      console.error("Error fetching Vimeo thumbnail:", error);
      return null;
    }
  };

  // Handle adding external video
  const handleAddExternalVideo = async () => {
    if (!externalVideoUrl.trim()) {
      setExternalVideoError("Please enter a video URL");
      return;
    }

    // Check if it's a YouTube URL
    const youtubeId = extractYouTubeVideoId(externalVideoUrl);
    if (youtubeId) {
      const embedUrl = `https://www.youtube.com/embed/${youtubeId}`;
      const thumbnailUrl = `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;

      if (onExternalVideoAdd) {
        onExternalVideoAdd({
          id: uuidv4(),
          url: embedUrl,
          thumbnailUrl,
          title: "YouTube Video",
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });
      }
      setExternalVideoUrl("");
      setShowExternalLinkInput(false);
      return;
    }

    // Check if it's a Vimeo URL
    const vimeoId = extractVimeoVideoId(externalVideoUrl);
    if (vimeoId) {
      const embedUrl = `https://player.vimeo.com/video/${vimeoId}`;
      const thumbnailUrl = await getVimeoThumbnail(vimeoId);

      if (onExternalVideoAdd) {
        onExternalVideoAdd({
          id: uuidv4(),
          url: embedUrl,
          thumbnailUrl: thumbnailUrl || undefined,
          title: "Vimeo Video",
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });
      }
      setExternalVideoUrl("");
      setShowExternalLinkInput(false);
      return;
    }

    // Check if it's a direct video URL
    if (isDirectVideoUrl(externalVideoUrl)) {
      if (onExternalVideoAdd) {
        onExternalVideoAdd({
          id: uuidv4(),
          url: externalVideoUrl,
          title: "External Video",
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });
      }
      setExternalVideoUrl("");
      setShowExternalLinkInput(false);
      return;
    }

    // If we get here, the URL is not recognized
    setExternalVideoError(
      "Please enter a valid YouTube, Vimeo, or direct MP4 URL"
    );
  };

  // Handle click on the upload area to trigger file input
  const handleUploadClick = () => {
    if (video?.url) {
      onRemoveVideo();
    } else if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="space-y-6">
      {!video?.url ? (
        // Upload area - shown when no video is uploaded
        <div className="space-y-4">
          {!showExternalLinkInput ? (
            // Upload options
            <div
              className={`border-2 border-dashed p-8 rounded-lg text-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20"
                  : "border-gray-300 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700"
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleUploadClick}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInputChange}
                accept="video/mp4,video/quicktime"
                className="hidden"
              />

              {isUploading ? (
                // Upload progress indicator
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="w-12 h-12 border-4 border-gray-300 dark:border-gray-700 border-t-indigo-500 rounded-full animate-spin mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-300 font-medium">
                    Uploading video...
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                    This may take a while depending on the file size
                  </p>
                </div>
              ) : (
                // Upload prompt
                <div className="flex flex-col items-center justify-center py-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-12 w-12 ${theme.text.muted} mb-4`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-gray-600 dark:text-gray-300 font-medium">
                    Drag and drop your video here
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                    or click to browse your files
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 text-xs mt-4">
                    Supported formats: MP4, MOV (Max size: 200MB)
                  </p>
                </div>
              )}
            </div>
          ) : (
            // External video link input
            <div className="border-2 border-dashed p-6 rounded-lg transition-colors border-gray-300 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
                Add External Video
              </h3>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="external-video-url"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Video URL
                  </label>
                  <div className="flex">
                    <input
                      type="url"
                      id="external-video-url"
                      value={externalVideoUrl}
                      onChange={handleExternalVideoUrlChange}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="flex-1 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 rounded-l-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleAddExternalVideo}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-r-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Add
                    </button>
                  </div>
                  {externalVideoError && (
                    <p className="mt-1 text-sm text-red-600">
                      {externalVideoError}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Supported: YouTube, Vimeo, or direct MP4 links
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Toggle between upload options */}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={toggleExternalLinkInput}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {showExternalLinkInput ? (
                <>Upload a file instead</>
              ) : (
                <>Use YouTube/Vimeo link instead</>
              )}
            </button>
          </div>
        </div>
      ) : (
        // Video preview area - shown when a video is uploaded
        <div className="space-y-4">
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Video Preview
            </h4>
            <div className="relative aspect-video rounded-lg overflow-hidden bg-black mb-4">
              <VideoPlayer
                video={video}
                className="w-full h-full object-contain"
              />

              {/* Remove button */}
              <button
                type="button"
                onClick={onRemoveVideo}
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                aria-label="Remove video"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Replace video button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleUploadClick}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Uploading...
                </>
              ) : (
                <>
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12"
                    />
                  </svg>
                  Replace Video
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Video details form - always shown but disabled when uploading */}
      <div className="mt-6 space-y-4">
        <div>
          <label
            htmlFor="video-title"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Video Title
          </label>
          <input
            type="text"
            id="video-title"
            value={video?.title || ""}
            onChange={handleTitleChange}
            className="mt-1 block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-800 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter video title"
            disabled={isUploading || !video?.url}
          />
        </div>

        <div>
          <label
            htmlFor="video-description"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Video Description
          </label>
          <textarea
            id="video-description"
            value={video?.description || ""}
            onChange={handleDescriptionChange}
            rows={3}
            className="mt-1 block w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-800 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter video description"
            disabled={isUploading || !video?.url}
          />
        </div>
      </div>
    </div>
  );
};

export default TutorialVideoUploader;
